import fastDiff from 'fast-diff';
import { fromEvent, merge, Observable } from 'rxjs';
import { filter, first, mergeMap, share, tap } from 'rxjs/operators';
import { hasCodeAncestor } from '../../helpers/dom/code';
import { isPhrasingContent, isPresentationalPhrasingContent } from '../../helpers/dom/content';
import { isInlineCodeElement } from '../../helpers/dom/inlineCode';
import {
  findAncestor,
  findNextNonWhitespaceSibling,
  hasAncestor,
  isElementWithTagName,
  isText,
} from '../../helpers/dom/node';
import { paintNodesInRange } from '../../helpers/dom/paint';
import { createPointWithStartOfRange } from '../../helpers/dom/point';
import { createRangeWithTextRange, createTextRangeWithRange } from '../../helpers/dom/range';
import { patchTextContent } from '../../helpers/dom/text';
import { createInsertionPoint, INSERTION_POINT_FILLER } from '../../helpers/insertionPoint';
import { InteractionAction, InteractionHandler, InteractionTrigger } from '../../interactionEvent';
import { Service } from '../base/service';
import { UndoService } from '../text/undoService';
import { ElementSelectionService } from './elementSelectionService';
import { createElementEffectService } from './elementService';

export type ElementInlineCodeServiceOptions = {
  onInteraction?: InteractionHandler;
};

export function createElementInlineCodeService(
  element$: Observable<Element | null>,
  selectionService: ElementSelectionService,
  undoService: UndoService,
  options: ElementInlineCodeServiceOptions = {},
): Service {
  return createElementEffectService(element$, element =>
    merge(
      applyInlineCodeAsYouTypeEffect(element, selectionService, undoService, options.onInteraction),
      moveCursorRightAfterBeginOfInlineCodeEffect(element, selectionService),
      moveCursorRightAfterEndOfInlineCodeEffect(element, selectionService),
    ).pipe(share()),
  );
}

function applyInlineCodeAsYouTypeEffect(
  element: Element,
  selectionService: ElementSelectionService,
  undoService: UndoService,
  onInteraction?: InteractionHandler,
) {
  return fromEvent<KeyboardEvent>(element, 'keydown').pipe(
    // Listen for the ` key being pressed, to establish typing intent.
    filter(event => event.key === '`'),
    // Listen for the next text/selection change that results from that key press.
    mergeMap(() => selectionService.changes$.pipe(first())),
    tap(() => {
      // Do we have a document?
      const { ownerDocument: document } = element;
      if (!document) {
        return;
      }

      // Do we have a cursor?
      const { container, selection } = selectionService;
      if (!container || !selection || !selection.isCollapsed) {
        return;
      }

      // Are we already in inline code or a code block?
      if (hasCodeAncestor(selection.focusNode, container)) {
        return;
      }

      // Find the current block container, which we consider to be the current line.
      const lineElement = findAncestor(selection.focusNode, node => !isPhrasingContent(node), container);
      if (!lineElement) {
        return;
      }

      // Find the cursor position relative to the line text (regardless of markup).
      const lineTextRange = createTextRangeWithRange(lineElement, selection.range);
      if (!lineTextRange || lineTextRange.startIndex !== lineTextRange.endIndex) {
        // This shouldn't happen since we have a collapsed cursor.
        return;
      }

      // Look for a closing backtick string immediately in front of the cursor.
      const lineText = lineElement.textContent || '';
      const textBeforeCursor = lineText.substring(0, lineTextRange.endIndex);
      const endBacktickStringMatch = textBeforeCursor.match(/`+$/);
      if (!endBacktickStringMatch) {
        return;
      }

      const backtickString = endBacktickStringMatch[0];
      const textBeforeEndBacktickString = textBeforeCursor.substring(
        0,
        textBeforeCursor.length - backtickString.length,
      );

      // Look for the last corresponding opening backtick string.
      const startBacktickStringRegExp = new RegExp(`(?<!\`)${backtickString}(?!\`)`, 'g');
      const startBacktickStringMatch = findLastMatch(textBeforeEndBacktickString, startBacktickStringRegExp);
      if (!startBacktickStringMatch) {
        return;
      }

      const startIndex = startBacktickStringMatch.index;
      const { endIndex } = lineTextRange;

      // Allow the user to undo this.
      undoService.takeAndCommitSnapshot();

      // Remove the backticks from the text.
      const textPatch: readonly fastDiff.Diff[] = [
        [fastDiff.EQUAL, lineText.slice(0, startIndex)],
        [fastDiff.DELETE, backtickString],
        [
          fastDiff.EQUAL,
          lineText.slice(startIndex + backtickString.length, endIndex - backtickString.length),
        ],
        [fastDiff.DELETE, backtickString],
        [fastDiff.EQUAL, lineText.slice(endIndex)],
      ];
      const didPatchSucceed = patchTextContent(lineElement, textPatch);
      if (!didPatchSucceed) {
        return;
      }

      const range = createRangeWithTextRange(lineElement, {
        startIndex,
        endIndex: endIndex - backtickString.length * 2,
      });
      if (!range) {
        // This shouldn't happen since we know these start/end indexes are in range.
        return;
      }

      // Apply the inline code style.
      let lastCodeElement: HTMLElement | undefined;
      paintNodesInRange(element, range, {
        shouldPaintNode: node =>
          isPhrasingContent(node) &&
          !hasAncestor(node, ancestor => isElementWithTagName(ancestor, 'code'), element),
        // Split phrasing content to favor one continuous span of inline code.
        shouldSplitElement: node => isPresentationalPhrasingContent(node),
        createPaintedElement: paintDocument => {
          const codeElement = paintDocument.createElement('code');
          lastCodeElement = codeElement;
          return codeElement;
        },
      });

      // Always add an insertion point with filler, to position the cursor after & outside the inline code.
      if (lastCodeElement && lastCodeElement.parentNode) {
        const insertionPoint = createInsertionPoint(document);
        insertionPoint.textContent = INSERTION_POINT_FILLER;
        lastCodeElement.parentNode.insertBefore(insertionPoint, lastCodeElement.nextSibling);
        selectionService.moveToOffsetInContainer(insertionPoint, 1);
      }

      if (onInteraction) {
        onInteraction({
          trigger: InteractionTrigger.KEYBOARD_TYPING,
          action: InteractionAction.SET_INLINE_CODE,
        });
      }
    }),
  );
}

function moveCursorRightAfterBeginOfInlineCodeEffect(
  element: Element,
  selectionService: ElementSelectionService,
) {
  return fromEvent<KeyboardEvent>(element, 'keydown').pipe(
    filter(event => event.key === 'ArrowRight' && !event.shiftKey),
    tap(event => {
      // Check we have a cursor (a collapsed selection).
      const range = selectionService.selection?.range;
      if (!range || !range.collapsed) {
        return;
      }

      // Check the cursor is right in front of inline code.
      const inlineCodeElement =
        isText(range.startContainer) &&
        range.startOffset === range.startContainer.length &&
        isInlineCodeElement(range.startContainer.nextSibling)
          ? range.startContainer.nextSibling
          : null;
      if (!inlineCodeElement) {
        return;
      }

      // We got this.
      event.preventDefault();

      moveCursorAfterBeginOfInlineCode(inlineCodeElement, selectionService);
    }),
  );
}

function moveCursorRightAfterEndOfInlineCodeEffect(
  element: Element,
  selectionService: ElementSelectionService,
) {
  return fromEvent<KeyboardEvent>(element, 'keydown').pipe(
    filter(event => event.key === 'ArrowRight' && !event.shiftKey),
    tap(event => {
      // Check we have a cursor (a collapsed selection).
      const range = selectionService.selection?.range;
      if (!range || !range.collapsed) {
        return;
      }

      // Check the cursor is in inline code.
      const inlineCodeElement = findAncestor(range.startContainer, isInlineCodeElement, element);
      if (!inlineCodeElement || !inlineCodeElement.parentNode) {
        return;
      }

      // Check the cursor is at the end of the inline code.
      const cursorPoint = createPointWithStartOfRange(range);
      const isAtEndOfInlineCode = cursorPoint.isAtEndOfNode(
        inlineCodeElement,
        node => !findNextNonWhitespaceSibling(node),
        element,
      );
      if (!isAtEndOfInlineCode) {
        return;
      }

      // We got this.
      event.preventDefault();

      // Insert an insertion point with filler after the inline code.
      moveCursorAfterEndOfInlineCode(inlineCodeElement, selectionService);
    }),
  );
}

function findLastMatch(text: string, regExp: RegExp): RegExpExecArray | null {
  let lastMatch: RegExpExecArray | null = null;
  let nextMatch: RegExpExecArray | null = null;

  while ((nextMatch = regExp.exec(text))) {
    lastMatch = nextMatch;
  }

  return lastMatch;
}

function moveCursorAfterBeginOfInlineCode(
  inlineCodeElement: HTMLElement,
  selectionService: ElementSelectionService,
) {
  if (!inlineCodeElement.ownerDocument) {
    return;
  }

  // Place an insertion point with filler at the start of the inline code.
  const insertionPoint = createInsertionPointWithFiller(inlineCodeElement.ownerDocument);
  inlineCodeElement.insertBefore(insertionPoint, inlineCodeElement.firstChild);

  // Move the cursor into the insertion point.
  selectionService.moveToOffsetInContainer(insertionPoint, 1);
}

function moveCursorAfterEndOfInlineCode(
  inlineCodeElement: HTMLElement,
  selectionService: ElementSelectionService,
) {
  if (!inlineCodeElement.ownerDocument || !inlineCodeElement.parentNode) {
    return;
  }

  // Place an insertion point with filler after the inline code.
  const insertionPoint = createInsertionPointWithFiller(inlineCodeElement.ownerDocument);
  inlineCodeElement.parentNode.insertBefore(insertionPoint, inlineCodeElement.nextSibling);

  // Move the cursor into the insertion point.
  selectionService.moveToOffsetInContainer(insertionPoint, 1);
}

function createInsertionPointWithFiller(document: Document) {
  const insertionPoint = createInsertionPoint(document);
  insertionPoint.textContent = INSERTION_POINT_FILLER;
  return insertionPoint;
}
